import React from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { cn } from '../../utils/cn';

/**
 * EmptyState — reusable empty-state component for all screens.
 *
 * Props:
 *  iconName      {string}   Lucide icon name (default: "Inbox")
 *  title         {string}   Bold heading text
 *  description   {string}   Muted sub-text
 *  ctaLabel      {string}   CTA button label (omit to hide button)
 *  ctaIcon       {string}   Optional Lucide icon for CTA button
 *  onCta         {func}     CTA click handler
 *  ctaVariant    {string}   Button variant (default: "elegant")
 *  size          {string}   "sm" | "md" (default) | "lg"
 *  className     {string}   Extra wrapper classes
 */
const EmptyState = ({
  iconName = 'Inbox',
  title = 'Aucun élément trouvé',
  description = 'Il n\'y a rien à afficher pour le moment.',
  ctaLabel,
  ctaIcon,
  onCta,
  ctaVariant = 'elegant',
  size = 'md',
  className,
}) => {
  const sizeConfig = {
    sm: {
      wrapper: 'py-8 px-4',
      iconBox: 'w-12 h-12',
      iconSize: 22,
      title: 'text-sm font-semibold',
      desc: 'text-xs',
    },
    md: {
      wrapper: 'py-12 px-6',
      iconBox: 'w-16 h-16',
      iconSize: 28,
      title: 'text-base font-semibold',
      desc: 'text-sm',
    },
    lg: {
      wrapper: 'py-16 px-8',
      iconBox: 'w-20 h-20',
      iconSize: 34,
      title: 'text-lg font-semibold',
      desc: 'text-sm',
    },
  };

  const cfg = sizeConfig?.[size] || sizeConfig?.md;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        cfg?.wrapper,
        className
      )}
    >
      {/* Icon container */}
      <div
        className={cn(
          'flex items-center justify-center rounded-2xl mb-4',
          'bg-brand-gold/10 border border-brand-gold/20',
          cfg?.iconBox
        )}
      >
        <Icon
          name={iconName}
          size={cfg?.iconSize}
          className="text-brand-gold"
          color="var(--color-brand-gold)"
        />
      </div>
      {/* Title */}
      <h3
        className={cn(
          'font-heading text-text-primary mb-1.5',
          cfg?.title
        )}
      >
        {title}
      </h3>
      {/* Description */}
      <p
        className={cn(
          'text-text-secondary max-w-xs leading-relaxed mb-5',
          cfg?.desc
        )}
      >
        {description}
      </p>
      {/* CTA Button */}
      {ctaLabel && onCta && (
        <Button
          variant={ctaVariant}
          size="sm"
          onClick={onCta}
          iconName={ctaIcon}
          iconPosition="left"
        >
          {ctaLabel}
        </Button>
      )}
    </div>
  );
};

export default EmptyState;
